import { ReactElement, useCallback, useMemo, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMapEvents } from 'react-leaflet';
import { DivIcon, LatLngBounds, LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { FitBounds } from '../../components/MapBoundsComponents';
import { getRuns } from '../../api/runs';
import { useSessionCalcData } from '../../hooks/useSessions';
import { useCreateGate } from '../../hooks/useGates';
import toast from 'react-hot-toast';

type GateFromRunState = {
  returnTo?: string;
  sessionId: string;
  existingGatesLat?: number[];
  existingGatesLon?: number[];
  hasCallback?: boolean;
};

type PickedGate = {
  lat: number;
  lon: number;
};

// Step 1: Pick a run  |  Step 2: Pick gates on map
type Step = 'select-run' | 'pick-gates';

function gateMarkerIcon(label: string, color: string): DivIcon {
  return new DivIcon({
    html: `
      <div style="position: relative; width: 100px; height: 100px;">
        <div style="position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);">
          <div style="width: 10px; height: 10px; background-color: ${color}; border-radius: 50%;
            border: 2px solid white; box-shadow: 0 1px 4px rgba(0,0,0,0.4);"></div>
        </div>
        <div style="position: absolute; left: 50%; bottom: 8px; transform: translateX(-50%);
          width: 2px; height: 26px; background-color: ${color}; opacity: 0.7;"></div>
        <div style="position: absolute; left: 50%; bottom: 34px; transform: translateX(-50%);
          background-color: ${color}; color: white; padding: 4px 12px; font-weight: bold;
          font-size: 12px; border-radius: 4px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          transform: translateX(-50%) rotate(-45deg); white-space: nowrap;">
          ${label}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 100],
    iconAnchor: [50, 94],
  });
}

function existingGateIcon(label: string): DivIcon {
  return new DivIcon({
    html: `
      <div style="position: relative; width: 100px; height: 100px;">
        <div style="position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);">
          <div style="width: 8px; height: 8px; background-color: #9ca3af; border-radius: 50%;
            border: 2px solid white; opacity: 0.6;"></div>
        </div>
        <div style="position: absolute; left: 50%; bottom: 6px; transform: translateX(-50%);
          width: 2px; height: 20px; background-color: #9ca3af; opacity: 0.4;"></div>
        <div style="position: absolute; left: 50%; bottom: 26px; transform: translateX(-50%);
          background-color: #9ca3af; color: white; padding: 3px 8px; font-weight: 600;
          font-size: 10px; border-radius: 4px; opacity: 0.6;
          transform: translateX(-50%) rotate(-45deg); white-space: nowrap;">
          ${label}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 100],
    iconAnchor: [50, 94],
  });
}

const GATE_A_COLOR = '#0f766e';
const GATE_B_COLOR = '#b91c1c';

function ClickHandler({ onMapClick }: { onMapClick: (lat: number, lon: number) => void }): null {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function findNearestPoint(
  clickLat: number,
  clickLon: number,
  lats: number[],
  lons: number[],
): { lat: number; lon: number; index: number } {
  let bestIndex = 0;
  let bestDist = Infinity;
  for (let i = 0; i < lats.length; i++) {
    const dLat = lats[i] - clickLat;
    const dLon = lons[i] - clickLon;
    const dist = dLat * dLat + dLon * dLon;
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }
  return { lat: lats[bestIndex], lon: lons[bestIndex], index: bestIndex };
}

export default function GateFromRunPicker(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as GateFromRunState | null;
  const sessionId = routeState?.sessionId ?? '';
  const returnTo = routeState?.returnTo;
  const existingGatesLat = routeState?.existingGatesLat;
  const existingGatesLon = routeState?.existingGatesLon;

  const [step, setStep] = useState<Step>('select-run');
  const [selectedRunMeta, setSelectedRunMeta] = useState<any>(null);
  const [runGpsData, setRunGpsData] = useState<{ lats: number[]; lons: number[] } | null>(null);
  const [isLoadingGps, setIsLoadingGps] = useState(false);
  const [gateA, setGateA] = useState<PickedGate | null>(null);
  const [gateB, setGateB] = useState<PickedGate | null>(null);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const createGate = useCreateGate();

  // Use cached session data for the run list (lightweight — no grid data)
  const { data: session, isPending: isSessionPending } = useSessionCalcData(sessionId);

  // Extract runs from session configs
  const runs = useMemo(() => {
    if (!session?.configs) return [];
    return session.configs
      .flatMap((config: any) => (config.runs || []).map((run: any) => ({
        ...run,
        configLetter: config.letter_number,
      })))
      .filter((run: any) => !run.deleted && run.run_status >= 4)
      .sort((a: any, b: any) => (a.run_number ?? 0) - (b.run_number ?? 0));
  }, [session]);

  const gpsLats: number[] = runGpsData?.lats ?? [];
  const gpsLons: number[] = runGpsData?.lons ?? [];

  const coordinates: LatLngExpression[] = useMemo(() => {
    const validLats = gpsLats.filter(Boolean);
    const validLons = gpsLons.filter(Boolean);
    return validLats.map((lat, i) => [lat, validLons[i]] as LatLngExpression);
  }, [gpsLats, gpsLons]);

  const mapBounds = useMemo(() => {
    if (coordinates.length === 0) return null;
    return new LatLngBounds(coordinates);
  }, [coordinates]);

  const handleSelectRun = useCallback(async (run: any) => {
    setSelectedRunMeta(run);
    setGateA(null);
    setGateB(null);
    setRunGpsData(null);
    setStep('pick-gates');
    setIsLoadingGps(true);

    try {
      const runData = await getRuns(`scope=grid_data&id=${run.id}`);
      const fullRun = runData?.[0];
      const lats = fullRun?.data_gridded?.GpsLat_AG20 ?? [];
      const lons = fullRun?.data_gridded?.GpsLon_AG20 ?? [];
      setRunGpsData({ lats, lons });
      setFitTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error loading GPS data:', error);
      toast.error('Failed to load GPS data for this run');
    } finally {
      setIsLoadingGps(false);
    }
  }, []);

  const handleMapClick = useCallback(
    (lat: number, lon: number) => {
      if (gpsLats.length === 0) return;

      const nearest = findNearestPoint(lat, lon, gpsLats, gpsLons);

      if (!gateA) {
        setGateA({ lat: nearest.lat, lon: nearest.lon });
      } else if (!gateB) {
        setGateB({ lat: nearest.lat, lon: nearest.lon });
      } else {
        // Both placed — replace Gate A and clear Gate B (restart)
        setGateA({ lat: nearest.lat, lon: nearest.lon });
        setGateB(null);
      }
    },
    [gateA, gateB, gpsLats, gpsLons],
  );

  const handleClearGates = useCallback(() => {
    setGateA(null);
    setGateB(null);
  }, []);

  const handleSaveGate = useCallback(async () => {
    if (!gateA || !gateB) return;
    setIsSaving(true);
    try {
      const newGate = await createGate.mutateAsync({
        id: crypto.randomUUID(),
        gates_lat: [gateA.lat, gateB.lat],
        gates_lon: [gateA.lon, gateB.lon],
        creation_mode: 0, // GatesCreation.MANUAL — protects from autocal overwrite
      });

      // If there's a callback waiting, call it with the new gate ID
      if (routeState?.hasCallback && (window as any).__gateSelectCallback) {
        (window as any).__gateSelectCallback(newGate.id);
      }

      toast.success('Gate created and assigned');

      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Error creating gate:', error);
      toast.error('Failed to create gate');
    } finally {
      setIsSaving(false);
    }
  }, [gateA, gateB, createGate, routeState, returnTo, navigate]);

  const handleBack = useCallback(() => {
    if (step === 'pick-gates') {
      setStep('select-run');
      setSelectedRunMeta(null);
      setRunGpsData(null);
      setGateA(null);
      setGateB(null);
    } else {
      navigate(-1);
    }
  }, [step, navigate]);

  // ── Step 1: Select a Run ──────────────────────────
  if (step === 'select-run') {
    return (
      <div className="cr-w-full">
        <SectionHeader
          title="Select a Run"
          isLoading={isSessionPending}
          actions={
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="cr-flex cr-items-center cr-gap-x-1.5 cr-whitespace-nowrap cr-rounded-md cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-font-semibold cr-text-[#03365D] cr-no-underline cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Back
            </button>
          }
        />

        <div className="cr-mt-6 cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
          <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
            Select a run from this session to use its GPS trace for placing gate markers.
          </p>

          {isSessionPending && (
            <div className="cr-flex cr-items-center cr-justify-center cr-py-12">
              <Spinner isFetching={true} />
            </div>
          )}

          {!isSessionPending && runs.length === 0 && (
            <div className="cr-py-8 cr-text-center cr-text-gray-500">
              No processed runs found in this session.
            </div>
          )}

          <div className="cr-flex cr-flex-col cr-gap-3">
            {runs.map((run: any) => (
              <div
                key={run.id}
                onClick={() => handleSelectRun(run)}
                className="cr-flex cr-cursor-pointer cr-items-center cr-justify-between cr-rounded-lg cr-border cr-border-gray-200 cr-p-4 cr-transition-colors hover:cr-border-cirrusBlue-200 hover:cr-bg-cirrusBlue-25"
              >
                <div className="cr-flex cr-items-start cr-gap-3">
                  <span className="cr-flex cr-h-6 cr-w-6 cr-items-center cr-justify-center cr-rounded-full cr-bg-cirrusBlue-100 cr-text-xs cr-font-semibold cr-text-white">
                    {run.run_number ?? '?'}
                  </span>
                  <div>
                    <p className="cr-font-medium">
                      Run {run.run_number ?? '?'}
                      {run.description ? ` — ${run.description}` : ''}
                    </p>
                  </div>
                </div>
                <Button onClick={(e) => { e.stopPropagation(); handleSelectRun(run); }}>
                  Select
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: Pick Gates on Map ─────────────────────
  const hasExistingGates =
    existingGatesLat && existingGatesLon && existingGatesLat.length >= 2 && existingGatesLon.length >= 2;

  const instructionText = !gateA
    ? 'Click on the GPS trace to place Gate A (start)'
    : !gateB
      ? 'Click on the GPS trace to place Gate B (end)'
      : 'Both gates placed. Save or click the trace to restart.';

  return (
    <div className="cr-w-full">
      <SectionHeader
        title="Place Gates"
        actions={
          <button
            type="button"
            onClick={handleBack}
            className="cr-flex cr-items-center cr-gap-x-1.5 cr-whitespace-nowrap cr-rounded-md cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-font-semibold cr-text-[#03365D] cr-no-underline cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Back to Run List
          </button>
        }
      />

      {/* Instruction bar */}
      <div className="cr-mb-4 cr-rounded-lg cr-bg-cirrusBlue-25 cr-px-4 cr-py-3 cr-text-sm cr-font-medium cr-text-[#03365D]">
        {instructionText}
      </div>

      {/* Map */}
      <div className="cr-relative cr-h-[500px] cr-overflow-hidden cr-rounded-xl cr-bg-white cr-shadow-sm">
        {isLoadingGps ? (
          <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
            <Spinner isFetching={true} />
            <span className="cr-ml-2 cr-text-sm cr-text-gray-500">Loading GPS data...</span>
          </div>
        ) : coordinates.length === 0 ? (
          <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-gray-500">
            This run has no GPS coordinates.
          </div>
        ) : (
          <MapContainer
            center={coordinates[Math.floor(coordinates.length / 2)]}
            zoom={15}
            scrollWheelZoom
            className="cr-h-full cr-w-full"
          >
            <FitBounds bounds={mapBounds} trigger={fitTrigger} />
            <ClickHandler onMapClick={handleMapClick} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* GPS trace */}
            <Polyline pathOptions={{ color: 'purple', weight: 3 }} positions={coordinates} />

            {/* Existing gates (faded) */}
            {hasExistingGates && (
              <>
                <Marker
                  position={[existingGatesLat![0], existingGatesLon![0]]}
                  icon={existingGateIcon('Old A')}
                />
                <Marker
                  position={[existingGatesLat![1], existingGatesLon![1]]}
                  icon={existingGateIcon('Old B')}
                />
              </>
            )}

            {/* New Gate A */}
            {gateA && (
              <Marker
                position={[gateA.lat, gateA.lon]}
                icon={gateMarkerIcon('Gate A', GATE_A_COLOR)}
              />
            )}

            {/* New Gate B */}
            {gateB && (
              <Marker
                position={[gateB.lat, gateB.lon]}
                icon={gateMarkerIcon('Gate B', GATE_B_COLOR)}
              />
            )}
          </MapContainer>
        )}
      </div>

      {/* Actions panel */}
      <div className="cr-mt-4 cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
        <div className="cr-flex cr-flex-wrap cr-items-center cr-justify-between cr-gap-4">
          <div className="cr-text-sm cr-text-gray-600">
            <p>
              <strong>Run {selectedRunMeta?.run_number ?? '?'}</strong>
              {selectedRunMeta?.description ? ` — ${selectedRunMeta.description}` : ''}
            </p>
            {gateA && (
              <p className="cr-mt-1">
                Gate A: {gateA.lat.toFixed(6)}, {gateA.lon.toFixed(6)}
              </p>
            )}
            {gateB && (
              <p>
                Gate B: {gateB.lat.toFixed(6)}, {gateB.lon.toFixed(6)}
              </p>
            )}
          </div>

          <div className="cr-flex cr-gap-2">
            <Button onClick={handleClearGates} disabled={!gateA && !gateB}>
              Clear
            </Button>
            <Button onClick={handleSaveGate} disabled={!gateA || !gateB || isSaving}>
              {isSaving ? 'Saving...' : 'Save Gate'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
